import { inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
} from '../config/environment.config';

/**
 * HTTP Request Options
 */
export interface HttpOptions {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?:
    | HttpParams
    | {
        [param: string]:
          | string
          | number
          | boolean
          | ReadonlyArray<string | number | boolean>;
      };
  observe?: 'body';
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
}

/**
 * Base API Service
 *
 * Enterprise-ready base class for all API services.
 * Provides consistent URL construction and HTTP methods.
 *
 * @example
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class UserService extends BaseApiService {
 *   constructor() {
 *     super('users'); // Base path: {apiUrl}/users
 *   }
 *
 *   getUser(id: string): Observable<User> {
 *     return this.get<User>(`/${id}`);
 *   }
 *
 *   createUser(data: CreateUserRequest): Observable<User> {
 *     return this.post<User>('', data);
 *   }
 * }
 * ```
 */
export abstract class BaseApiService {
  protected readonly http = inject(HttpClient);
  protected readonly environment: EnvironmentConfig =
    inject(ENVIRONMENT_CONFIG);
  protected readonly baseUrl: string;

  /**
   * @param resourcePath - The resource path to append to apiUrl (e.g., 'auth', 'users', 'bible')
   */
  constructor(resourcePath: string) {
    // Construct full base URL in a safe way (avoid double slashes)
    // Ensure `environment.apiUrl` does not end with a slash
    const apiUrlClean = this.environment.apiUrl.replace(/\/+$/, '');
    const resourceClean = resourcePath
      ? '/' + resourcePath.replace(/^\/+|\/+$/g, '')
      : '';
    // Result examples:
    //  apiUrlClean = 'https://api.example.com/api/v1'
    //  resourceClean = '/bible' or ''
    //  baseUrl = 'https://api.example.com/api/v1/bible' or 'https://api.example.com/api/v1'
    this.baseUrl = `${apiUrlClean}${resourceClean}`;
  }

  /**
   * Build full URL for endpoint
   * @param endpoint - Endpoint path (e.g., '/login', '/chapters')
   */
  protected buildUrl(endpoint: string): string {
    const cleanEndpoint = endpoint.replace(/^\//, '');
    return cleanEndpoint ? `${this.baseUrl}/${cleanEndpoint}` : this.baseUrl;
  }

  /**
   * HTTP GET request
   */
  protected get<T>(endpoint: string, options?: HttpOptions): Observable<T> {
    return this.http.get<T>(this.buildUrl(endpoint), options);
  }

  /**
   * HTTP POST request
   */
  protected post<T>(
    endpoint: string,
    body: object | FormData,
    options?: HttpOptions
  ): Observable<T> {
    return this.http.post<T>(this.buildUrl(endpoint), body, options);
  }

  /**
   * HTTP PUT request
   */
  protected put<T>(
    endpoint: string,
    body: object | FormData,
    options?: HttpOptions
  ): Observable<T> {
    return this.http.put<T>(this.buildUrl(endpoint), body, options);
  }

  /**
   * HTTP PATCH request
   */
  protected patch<T>(
    endpoint: string,
    body: object | FormData,
    options?: HttpOptions
  ): Observable<T> {
    return this.http.patch<T>(this.buildUrl(endpoint), body, options);
  }

  /**
   * HTTP DELETE request
   */
  protected delete<T>(endpoint: string, options?: HttpOptions): Observable<T> {
    return this.http.delete<T>(this.buildUrl(endpoint), options);
  }

  /**
   * Get API URL for this service
   * Useful for debugging or displaying to users
   */
  protected getApiUrl(): string {
    return this.baseUrl;
  }
}
